import * as Sentry from '@sentry/react';
import { StytchB2BProvider } from '@stytch/react/b2b';
import { StytchB2BUIClient } from '@stytch/vanilla-js/b2b';
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
} from '@tanstack/react-query';
import { AgChartsCommunityModule } from 'ag-charts-enterprise';
import {
  ModuleRegistry,
  ColumnAutoSizeModule,
  RowAutoHeightModule,
  RowStyleModule,
  PaginationModule,
  CellStyleModule,
  HighlightChangesModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  CustomFilterModule,
  QuickFilterModule,
  RowSelectionModule,
  CustomEditorModule,
  CsvExportModule,
  ColumnApiModule,
  ClientSideRowModelModule,
  provideGlobalGridOptions,
  RenderApiModule,
  ValidationModule,
  RowApiModule,
  ClientSideRowModelApiModule,
  CellApiModule,
  TextEditorModule,
  NumberEditorModule,
  PinnedRowModule,
  ScrollApiModule,
  ExternalFilterModule,
  DateEditorModule,
  CheckboxEditorModule,
} from 'ag-grid-community';
import {
  LicenseManager as GridLicenseManager,
  SetFilterModule,
  MultiFilterModule,
  TreeDataModule,
  ColumnMenuModule,
  ContextMenuModule,
  ExcelExportModule,
  IntegratedChartsModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  ClipboardModule,
  StatusBarModule,
  MasterDetailModule,
} from 'ag-grid-enterprise';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import minMax from 'dayjs/plugin/minMax';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { resources } from 'i18n/resources';
import i18n from 'i18next';
import { lazy, Suspense, useEffect } from 'react';
import ReactGA from 'react-ga4';
import { initReactI18next } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';
import { Routes } from 'routes/Routes';
import { MaximorThemeProvider } from 'theme/MaximorThemeProvider';

import { Loader } from 'components/loading/Loader';
import { SessionProvider } from 'components/providers/SessionProvider';

import { NotificationProvider } from 'hooks/utils/useNotification';

import { initializeAnalytics, trackEvent } from 'utils/analytics';
import { getErrorProperties } from 'utils/errorHandling';
import { Logger } from 'utils/logger';

import { useTimeZoneStore } from 'store/time-zone.store';

//css files
import 'src/App.css';

dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(quarterOfYear);
dayjs.extend(localizedFormat);
dayjs.extend(isBetween);
dayjs.extend(relativeTime);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(minMax);

i18n.use(initReactI18next).init({
  lng: 'en',
  fallbackLng: 'en',
  resources,
  // Preload common namespace to reduce initial load time and use it as a fallback
  ns: ['common'],
  defaultNS: 'common',
  interpolation: {
    escapeValue: false,
  },
  saveMissing: true,
  missingKeyHandler: function (lng, ns, key, fallbackValue) {
    Logger.error(
      `Missing translation key: ${key} in namespace: ${ns} for language: ${lng} with fallback value: ${fallbackValue}`
    );
  },
});

ModuleRegistry.registerModules([
  ColumnAutoSizeModule,
  RowAutoHeightModule,
  RowStyleModule,
  PaginationModule,
  CellStyleModule,
  HighlightChangesModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  CustomFilterModule,
  QuickFilterModule,
  RowSelectionModule,
  CustomEditorModule,
  CsvExportModule,
  ColumnApiModule,
  ClientSideRowModelModule,
  ClientSideRowModelApiModule,
  SetFilterModule,
  MultiFilterModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  TreeDataModule,
  ColumnMenuModule,
  ContextMenuModule,
  ExcelExportModule,
  RenderApiModule,
  RowApiModule,
  ClientSideRowModelApiModule,
  CellApiModule,
  TextEditorModule,
  NumberEditorModule,
  PinnedRowModule,
  IntegratedChartsModule.with(AgChartsCommunityModule),
  ScrollApiModule,
  ClipboardModule,
  ExternalFilterModule,
  ValidationModule,
  DateEditorModule,
  CheckboxEditorModule,
  StatusBarModule,
  MasterDetailModule,
]);

if (import.meta.env.MODE !== 'production') {
  ModuleRegistry.registerModules([ValidationModule]);
}

GridLicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID_LICENSE_KEY);

provideGlobalGridOptions({
  theme: 'legacy',
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      Sentry.captureException(error);

      const tracking = query.meta?.tracking as
        | {
            eventName: string;
            additionalProperties?: Record<string, unknown>;
          }
        | undefined;

      if (tracking?.eventName) {
        const errorProperties = getErrorProperties(error);

        trackEvent(tracking.eventName, {
          ...errorProperties,
          ...tracking.additionalProperties,
        });
      }
    },
  }),
});

const ConfigCatWrapper = lazy(() =>
  import('layout/ConfigCatProvider').then((module) => ({
    default: module.ConfigCatWrapper,
  }))
);

// Initialize Stytch B2B client as a singleton to prevent multiple instances during HMR
declare global {
  interface Window {
    __stytchClient?: StytchB2BUIClient;
  }
}

const stytchClient =
  window.__stytchClient ??
  (window.__stytchClient = new StytchB2BUIClient(
    import.meta.env.VITE_STYTCH_PUBLIC_TOKEN!
  ));

export default function App() {
  const { timeZone } = useTimeZoneStore();

  useEffect(() => {
    ReactGA.initialize('G-LMMM53ZM8N');
    initializeAnalytics();
  }, []);

  useEffect(() => {
    dayjs.tz.setDefault(timeZone);
  }, [timeZone]);

  return (
    <div className="App h-full w-full">
      <StytchB2BProvider stytch={stytchClient}>
        <MaximorThemeProvider>
          <QueryClientProvider client={queryClient}>
            <NotificationProvider>
              <SessionProvider>
                <Suspense fallback={null}>
                  <ConfigCatWrapper>
                    <div className="h-full w-full">
                      <Loader />
                      <BrowserRouter>
                        <Routes />
                      </BrowserRouter>
                    </div>
                  </ConfigCatWrapper>
                </Suspense>
              </SessionProvider>
            </NotificationProvider>
          </QueryClientProvider>
        </MaximorThemeProvider>
      </StytchB2BProvider>
    </div>
  );
}
